import React from 'react';
import styled from 'styled-components';
import type { Components } from 'react-markdown';
import type { Node } from 'unist';

// Inline citation markers for Insights answers. The agent tags a figure it drew
// from an aggregate_metric / find_outliers result with a `[n]` marker (n = that
// source's citationIndex). Here we turn those markers into clickable anchors to
// the matching row in the Sources footer, WITHOUT touching any other text.
//
// The transform runs on the markdown AST (mdast), so it only ever splits plain
// `text` nodes — code spans and fenced blocks are distinct node types and are
// left untouched. We also only linkify indices that a real source carries, so a
// bracketed year ("[2020]") or an array index the prose happens to contain is
// never mistaken for a citation.

export const CITE_HREF_PREFIX = '#insights-cite-';

// Minimal mdast shape we touch — extends the unist Node so a plain remark tree
// (whose nodes only guarantee `type`) is assignable without a cast.
interface MdastNode extends Node {
  value?: string;
  url?: string;
  children?: MdastNode[];
}

// Return the citation index encoded in a marker href, or null if it isn't one.
export const parseCitationHref = (href?: string): number | null => {
  if (!href || !href.startsWith(CITE_HREF_PREFIX)) {
    return null;
  }
  const n = Number(href.slice(CITE_HREF_PREFIX.length));
  return Number.isInteger(n) ? n : null;
};

// Split a text value on `[n]` markers whose n is a known citation index,
// emitting link nodes for the markers and text nodes for everything else.
const splitTextNode = (value: string, valid: Set<number>): MdastNode[] => {
  const out: MdastNode[] = [];
  const re = /\[(\d+)\]/g;
  let last = 0;
  let match = re.exec(value);
  while (match !== null) {
    const n = Number(match[1]);
    if (valid.has(n)) {
      if (match.index > last) {
        out.push({ type: 'text', value: value.slice(last, match.index) });
      }
      out.push({
        type: 'link',
        url: `${CITE_HREF_PREFIX}${n}`,
        children: [{ type: 'text', value: `[${n}]` }],
      });
      last = match.index + match[0].length;
    }
    match = re.exec(value);
  }
  if (out.length === 0) {
    return [{ type: 'text', value }];
  }
  if (last < value.length) {
    out.push({ type: 'text', value: value.slice(last) });
  }
  return out;
};

// remark plugin factory. Walks the tree, replacing `[n]` markers inside text
// nodes with citation link nodes. Never descends into an existing link (no
// nested links); code nodes have no text children, so they're inherently safe.
export const remarkInsightsCitations =
  (options: { validIndices: Set<number> }) =>
  (tree: Node): void => {
    const valid = options.validIndices;
    if (valid.size === 0) {
      return;
    }
    const walk = (node: MdastNode): void => {
      if (!node.children) {
        return;
      }
      const next: MdastNode[] = [];
      node.children.forEach((child) => {
        if (child.type === 'text' && child.value) {
          next.push(...splitTextNode(child.value, valid));
          return;
        }
        if (child.type !== 'link') {
          walk(child);
        }
        next.push(child);
      });
      node.children = next;
    };
    walk(tree);
  };

const Marker = styled.button`
  display: inline;
  padding: 0;
  margin: 0 1px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.7em;
  line-height: 0;
  vertical-align: super;
  color: var(--color-text-link);
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

// The rendered `[n]` marker — a superscript button that reveals and highlights
// its source in the footer.
export const CitationMarker = ({
  index,
  onClick,
}: {
  index: number;
  onClick: (index: number) => void;
}) => (
  <Marker
    type="button"
    onClick={() => onClick(index)}
    data-testid={`insights-citation-${index}`}
    aria-label={`Source ${index}`}
  >
    {`[${index}]`}
  </Marker>
);

// The react-markdown `components` map: render citation anchors as markers and
// everything else as a normal, safe external link.
export const buildCitationComponents = ({
  onCiteClick,
}: {
  onCiteClick: (index: number) => void;
}): Components => ({
  a: ({ href, children }) => {
    const index = parseCitationHref(href);
    if (index !== null) {
      return <CitationMarker index={index} onClick={onCiteClick} />;
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
});
